/**
 * Follow a person (collaboration phase 2): while you follow someone in the project (presenceClient.followPerson, from
 * the header's presence list), the page opens the file they are in, in their service, and MonacoFileEditor scrolls with
 * their cursor. A bar says whom you follow, with Stop. Following stops when you type (the editor) or open another file
 * yourself (presenceClient.setPresenceView), or when they leave.
 */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useProjectPresence from '../../hooks/useProjectPresence';
import { expectFollowPath, followPerson, getOwnView, presencePath } from '../../utils/presenceClient';
import { firstName, followedPerson, followTarget, splitVfsPath } from '../../utils/presenceModel';
import { inkFor } from '../../utils/participantColors';
import { ParticipantMark } from '../presence/Presence';
import './Collab.css';

const RETRIES_MS = [0, 700, 1800, 3500];

export default function FollowController({ projectId, onOpenFile }) {
  const snapshot = useProjectPresence(projectId);
  const person = followedPerson(snapshot);
  const target = followTarget(person);
  const targetPath = target?.path || null;
  const timers = useRef([]);

  // They left (or hid themselves): follow mode ends.
  useEffect(() => {
    if (projectId && snapshot.following && snapshot.status === 'open' && !person && (snapshot.people || []).length > 0) {
      followPerson(projectId, null);
    }
  }, [projectId, snapshot.following, snapshot.status, snapshot.people, person]);

  // Their file changed: open it (the tree may need a moment after a service switch, so a few tries).
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (!projectId || !targetPath || !onOpenFile) { return undefined; }
    expectFollowPath(projectId, targetPath);
    const { service, file } = splitVfsPath(targetPath);
    RETRIES_MS.forEach((ms, index) => {
      timers.current.push(setTimeout(() => {
        if (getOwnView(projectId, 'editor')?.path === presencePath(targetPath)) { return; }
        onOpenFile({ service, filePath: file, path: targetPath, lineNumber: target?.cursor?.line || null, first: index === 0 });
      }, ms));
    });
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // Only when the file changes; the cursor is followed by the editor itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, targetPath]);

  if (!person) { return null; }
  return (
    <div className="collab-follow-bar" data-testid="follow-bar" role="status" style={{ '--presence-ink': inkFor(person.slot) }}>
      <ParticipantMark initials={person.initials} size={18} slot={person.slot} />
      <span>
        Following <strong>{firstName(person.name)}</strong>
        {targetPath ? <span className="collab-muted"> · {targetPath.split('/').pop()}{target?.cursor ? `:${target.cursor.line}` : ''}</span> : <span className="collab-muted"> · no file open</span>}
        <span className="collab-muted collab-small"> · typing or opening another file stops it</span>
      </span>
      <button className="collab-link" onClick={() => followPerson(projectId, null)} type="button">Stop</button>
    </div>
  );
}

FollowController.propTypes = {
  onOpenFile: PropTypes.func,
  projectId: PropTypes.string,
};
